import type { ReactNode } from 'react'
import type { ShownVendorReviewDto, VendorReviewSummaryDto } from '@lib/dto/vendor-review.dto'
import { ratingLabel, ratingStars, reviewerLine, reviewsShownLabel, reviewSummaryLabel } from '@lib/helpers/vendor-reviews'
import { cn } from '@/lib/utils'

interface StarsProps {
  rating: number
  className?: string
}

/**
 * A rating as five stars, filled up to it, read out as "4 out of 5 stars": an
 * image to assistive technology, since a label on a plain span isn't read.
 */
export function Stars({ rating, className }: StarsProps) {
  return (
    <span role="img" aria-label={ratingLabel(rating)} className={cn('text-primary', className)}>
      {ratingStars(rating)}
    </span>
  )
}

interface SummaryProps {
  summary: VendorReviewSummaryDto
}

/**
 * "★ 4.8 out of 5 · 23 reviews": a company's published reviews summed up. One
 * star beside the number: a row of whole stars would round 4.5 up to five.
 */
export function ReviewSummary({ summary }: SummaryProps) {
  return (
    <p className="m-0 text-sm text-ink">
      <span className="text-primary" aria-hidden>
        ★
      </span>{' '}
      {reviewSummaryLabel(summary)}
    </p>
  )
}

interface ItemProps {
  review: ShownVendorReviewDto
  /** The company's name, to show its published reply under the review as its own; left out where the reply is shown otherwise. */
  replyFrom?: string
  /** Anything shown under the review, such as when it was published. */
  children?: ReactNode
  className?: string
}

interface MoreProps {
  shown: number
  summary: VendorReviewSummaryDto
}

/** "Showing the 20 most recent of 23 reviews.", only when there are more than shown. */
export function MoreReviews({ shown, summary }: MoreProps) {
  if (summary.count <= shown) return null
  return <p className="m-0 text-xs text-ink-muted">{reviewsShownLabel(shown, summary.count)}</p>
}

/**
 * One published review, as everyone who may read it sees it
 * (docs/vendor-growth.spec.md, "Reviews"): its stars, the reviewer as the
 * review shows them, and what they said.
 */
export function ReviewItem({ review, replyFrom, children, className }: ItemProps) {
  return (
    <li className={cn('space-y-1', className)}>
      <p className="m-0 text-xs text-ink-muted">
        <Stars rating={review.rating} /> {reviewerLine(review.reviewer, review.month)}
      </p>
      <p className="m-0 whitespace-pre-line text-sm text-ink">{review.body}</p>
      {replyFrom === undefined || review.reply === null ? null : (
        <div className="ml-3 space-y-0.5 border-l-2 border-line pl-3">
          <p className="m-0 text-xs font-medium text-ink-muted">Response from {replyFrom}</p>
          <p className="m-0 whitespace-pre-line text-sm text-ink">{review.reply.body}</p>
        </div>
      )}
      {children}
    </li>
  )
}
